import { useEffect, useState } from "react";
import "../styles/profile-card.scss";
import { GuiButton } from "./global-ui/gui-button";
import { appState } from "../redux/redux-config";
import { useSelector } from "react-redux";
import api from "../api/api";
import { useOnFirstLoad } from "../utilities/hooks";
import { formatPhone, useUpdateState } from "../utilities/utilities";
import CircularProgress from "../actions/CircularProgress";
import { GuiTooltip } from "./global-ui/gui-tooltip";
import { isMobile, UserRole } from "../utilities/constants";
import pushNotifications from "../actions/push-notification-actions";
import { isPushSupported } from "../utilities/push-notifications";

export default function ProfileCard({
  profileUserId,
  onEditClick,
  isEditing,
  isComponentDisabled,
  onProfileEdit,
  onAddContact,
  onEditContact,
  onEditPreference,
  isUserViewOnly = false,
  showToastViewOnly,
  loginUserType,
}) {
  const [loading, setLoading] = useState(true);
  const [loaderError, setError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const person = JSON.parse(localStorage.getItem("selectedPerson"));
  let loginUser = useSelector((store) => store.user);
  let pushEnabled = useSelector(
    (store) => store.pushNotifications?.enabled ?? false,
  );
  const showPushNotifications = !profileUserId && isPushSupported();

  useEffect(() => {
    if (showPushNotifications) pushNotifications.checkStatus();
  }, [showPushNotifications]);

  async function handlePushToggle() {
    if (pushEnabled) {
      await pushNotifications.disable();
    } else {
      await pushNotifications.enable();
    }
  }

  const [state, updateState] = useUpdateState({
    primaryEmail: "",
    notificationEmail: "",
    contacts: [],
    addressList: [],
    voice: "",
    address: "",
    name: "",
    role: "",
    orgName: "",
    orgId: "",
    userId: "",
    smsEnableNotify: false,
    profilePhotoURL: "",
    replyToEmail: "",
    phoneCode: "",
    phonePin: "",
  });

  function onEditPreferenceClick() {
    const payload = {
      selectedVoiceId: voice,
      enableEmail: state.smsEnableNotify,
      email: state.notificationEmail,
      replyToEmail: state.replyToEmail,
      phoneCode: state.phoneCode,
      phonePin: state.phonePin,
    };
    onEditPreference(payload);
  }

  useEffect(() => {
    let timeoutId;
    if (loading) {
      timeoutId = setTimeout(() => {
        setLoading(false);
        setError(true);
      }, 15000);
    }
    // Cleanup the timeout on component unmount or when loading changes
    return () => clearTimeout(timeoutId);
  }, [loading]);

  const optimizeImageFromUrl = (imageUrl, maxWidth = 200, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      const timeout = setTimeout(() => {
        reject(new Error("Image loading timed out"));
      }, 5000); // 5 sec timeout

      img.onload = () => {
        clearTimeout(timeout);

        const scale = maxWidth / img.width;
        const canvas = document.createElement("canvas");
        canvas.width = maxWidth;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            const optimizedUrl = URL.createObjectURL(blob);
            resolve(optimizedUrl);
          },
          "image/jpeg",
          quality,
        );
      };

      img.onerror = (e) => {
        clearTimeout(timeout);
        reject(e);
      };

      img.src = imageUrl;
    });
  };

  const fetchUserProfile = async () => {
    const token = appState().user.token;
    setError(false);
    try {
      let result;
      if (profileUserId) {
        result = await api.user.getUserProfileForSpecificUser(
          token,
          profileUserId,
        );
      } else {
        result = await api.user.getUserProfile(token);
      }

      if (!result.success) {
        setError(true);
        setLoading(false);
        return;
      }

      updateState({
        role: result.role,
        userId: result.userId,
        profilePhotoURL: result.profilePhotoURL,
      });

      // Optimize the profile photo in the background so it doesn't block
      // rendering the rest of the card; swap it in once ready.
      if (result.profilePhotoURL) {
        optimizeImageFromUrl(result.profilePhotoURL)
          .then((optimizedUrl) => {
            updateState({ profilePhotoURL: optimizedUrl });
          })
          .catch((e) => {
            console.warn("Failed to optimize image, using original:", e);
          });
      }

      updateState({ orgName: result?.customerName, orgId: result?.customerId });

      const name = `${result?.firstName} ${result?.lastName}`;
      updateState({ name });

      updateState({
        primaryEmail: result.emailAddress,
      });
      const preferences = result.preferences || {};
      const notificationEmailContact =
        preferences.textNotificationEmail?.smsNotifyEmail || "";
      const replyToEmailContact = preferences.replyToEmail || "";

      updateState({
        notificationEmail:
          notificationEmailContact.trim() === ""
            ? result.emailAddress
            : notificationEmailContact,
        replyToEmail: replyToEmailContact,
        smsEnableNotify: preferences.textNotificationEmail?.smsEnableNotify,
        phoneCode: preferences.phoneCode || "",
        phonePin: preferences.phonePin || "",
      });

      const contacts = result.contacts?.contacts || {};
      const addressList = result.addressList?.addressList || {};

      updateState({ contacts, addressList });

      const voicePreferences = preferences.voice || {};
      const voice = voicePreferences.langDesc
        ? `${voicePreferences.langDesc} - ${voicePreferences.voice}`
        : "-";

      updateState({ voice });

      const address =
        [result.address, result.city, result.state, result.zip]
          .filter((val) => val && val.trim())
          .join(" ")
          .trim() || "-";

      updateState({ address });
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useOnFirstLoad(() => {
    fetchUserProfile();
  });

  const {
    primaryEmail,
    notificationEmail,
    replyToEmail,
    voice,
    name,
    role,
    orgName,
    orgId,
    userId,
    phoneCode,
    phonePin,
  } = state;
  const editContactDetails = (
    contactTypeName,
    contactId = "",
    selectedContacts = "",
  ) => {
    if (contactTypeName === "Address") {
      onEditContact({
        contactTypeName: contactTypeName,
        userId: userId,
      });
    } else {
      const contact = selectedContacts.find(
        (contact) => contact.contactId === contactId,
      );
      onEditContact(contact);
    }
  };

  const handleAddressDetails = (items) => {
    onEditContact(items);
  };
  return (
    <div
      className={`${loaderError && "loader-error"} profile-card ${
        isComponentDisabled && "disabled"
      }`}
    >
      {loading && (
        <CircularProgress
          className="circular-progress--dashboard"
          label="Loading profile..."
        />
      )}
      {!loading && loaderError && (
        <span>Failed to load data, please try again later.</span>
      )}
      {!loading && !loaderError && (
        <>
          <div className={`profile-header ${isEditing ? "disabled" : ""}`}>
            <div className="background-curve"></div>
            <div className="profile-image-container">
              {!imageLoaded && (
                <div className="initials">
                  {name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </div>
              )}

              {state?.profilePhotoURL && (
                <img
                  src={state?.profilePhotoURL}
                  alt="Profile"
                  className={`profile-image ${
                    imageLoaded ? "visible" : "hidden"
                  }`}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageLoaded(false)}
                />
              )}
            </div>
            <div className="profile-info">
              <div className="profile-name">
                <h4>{name}</h4>
                <div
                  className="edit-icon cursor-pointer"
                  onClick={isUserViewOnly ? showToastViewOnly : onProfileEdit}
                  style={
                    isUserViewOnly
                      ? {
                          opacity: 0.6,
                        }
                      : {}
                  }
                >
                  {isMobile() ? (
                    <i className="fa fa-pencil"></i>
                  ) : (
                    <GuiTooltip
                      tooltip={
                        loginUserType === UserRole.Member
                          ? "Your profile is view-only. To update any detail, please contact an administrator."
                          : "Edit Profile"
                      }
                      placement="right"
                    >
                      <i className="fa fa-pencil"></i>
                    </GuiTooltip>
                  )}
                </div>
              </div>
              <div className="org-name org-inline">
                <i className="fa fa-building"></i>
                <p className="organization org-gap">{orgName}</p>
                {loginUserType !== UserRole.Member && (
                  <>
                    <i className="fas fa-user"></i>
                    <p className="organization org-gap">{orgId}</p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="profile-card-body">
            <div className={`permissions ${isEditing ? "disabled" : ""}`}>
              <p className="section-title">Permissions</p>
              <div className="section-body">
                <div>
                  <i className="fa fa-key"></i>
                  <span className="section-body-title">My Role</span>
                </div>
                <GuiButton
                  className="role-badge"
                  onClick={(e) => e.preventDefault()}
                >
                  {role}
                </GuiButton>
              </div>
            </div>

            <div className={`primary-contact ${isEditing ? "disabled" : ""}`}>
              <p className="section-title">Primary Contact</p>
              <div className="section-body">
                <div>
                  <i className="fa fa-envelope"></i>
                  <span className="section-body-title">Email</span>
                </div>
                <span className="section-body-title">{primaryEmail}</span>
              </div>
            </div>
            <div className="profile-horizontal-divider"></div>
            <div className="contact-methods">
              <div className="section-title">
                <div className="section-title-header">
                  <div>Contact Methods</div>
                  <div
                    className="add-edit-btns"
                    style={
                      isUserViewOnly
                        ? {
                            opacity: 0.6,
                          }
                        : {}
                    }
                  >
                    <GuiButton
                      className="add-edit-contact"
                      onClick={
                        isUserViewOnly ? showToastViewOnly : onAddContact
                      }
                    >
                      <GuiTooltip
                        tooltip="Add Contact Methods"
                        placement="left"
                      >
                        <>
                          <i className="fa fa-add"></i> Contact
                        </>
                      </GuiTooltip>
                    </GuiButton>
                    <GuiButton
                      className={`add-edit-contact ${
                        isEditing ? "cancel-edit-active" : ""
                      }`}
                      onClick={isUserViewOnly ? showToastViewOnly : onEditClick}
                    >
                      {isMobile() ? (
                        <>
                          {isEditing ? (
                            <div>
                              {" "}
                              <i className="fa fa-close"></i>Cancel Edit
                            </div>
                          ) : (
                            <div>
                              <i className="fa fa-pencil"></i> Edit
                            </div>
                          )}
                        </>
                      ) : (
                        <GuiTooltip
                          tooltip="Edit Contact Methods"
                          placement="right"
                        >
                          {isEditing ? (
                            <div>
                              {" "}
                              <i className="fa fa-close"></i>Cancel Edit
                            </div>
                          ) : (
                            <div>
                              <i className="fa fa-pencil"></i> Edit
                            </div>
                          )}
                        </GuiTooltip>
                      )}
                    </GuiButton>
                  </div>
                </div>
              </div>

              {state?.contacts?.length > 0 &&
                state?.contacts?.map((items, index) => (
                  <div className="section-body" key={items?.contactId}>
                    <div>
                      {(() => {
                        switch (items.customTypeMethod) {
                          case "Email":
                            return <i className="fa fa-envelope"></i>;
                          case "Home Phone":
                            return <i className="fa fa-phone"></i>; // Corrected icon
                          case "Work Phone":
                            return <i className="fa fa-phone-office"></i>;
                          default:
                            return <i className="fa fa-mobile-alt"></i>; // Default icon
                        }
                      })()}

                      <span className="section-body-title">
                        {items?.contactTypeName}
                      </span>
                    </div>
                    <span className="section-body-title">
                      {/* {items.contactTypeName === "Email"
                        ? addHyphenation(items.phone)
                        : formatPhone(items.phone, false)} */}
                      {items?.customTypeMethod === "Email"
                        ? items?.phone
                        : formatPhone(items?.phone, false)}

                      {isEditing && state?.contacts?.length > 0 && (
                        <i
                          className="fa fa-pencil ml-2 cursor-pointer"
                          onClick={() =>
                            editContactDetails(
                              items?.contactTypeName,
                              items?.contactId,
                              state?.contacts,
                            )
                          }
                        ></i>
                      )}
                    </span>
                  </div>
                ))}

              {state?.addressList?.length > 0 &&
                state.addressList?.map((items, index) => (
                  <div className="section-body" key={items?.id}>
                    <div>
                      <i className="fa fa-map-marker-alt"></i>
                      <span className="section-body-title">
                        {items?.contactTypeName}
                      </span>
                    </div>
                    <span className="section-body-title">
                      <div className="word-break">
                        {items?.address}, {items?.city}, {items?.state},{" "}
                        {items?.zip}
                      </div>
                      {isEditing && (
                        <div>
                          <i
                            className="fa fa-pencil ml-2 cursor-pointer"
                            onClick={() => handleAddressDetails(items)}
                          ></i>
                        </div>
                      )}
                    </span>
                  </div>
                ))}
            </div>
            <div className="profile-horizontal-divider"></div>
            <div className={`preferences ${isEditing ? "disabled" : ""}`}>
              <div className="section-title">
                <div>Preferences</div>
                <div
                  style={
                    isUserViewOnly
                      ? {
                          opacity: 0.6,
                        }
                      : {}
                  }
                >
                  <GuiButton
                    className="add-edit-contact"
                    onClick={
                      isUserViewOnly ? showToastViewOnly : onEditPreferenceClick
                    }
                  >
                    {isMobile() ? (
                      <div>
                        <i className="fa fa-pencil"></i>
                        Edit
                      </div>
                    ) : (
                      <GuiTooltip tooltip="Edit Preferences" placement="right">
                        <div>
                          <i className="fa fa-pencil"></i>
                          Edit
                        </div>
                      </GuiTooltip>
                    )}
                  </GuiButton>
                </div>
              </div>

              <div className="section-body">
                <div>
                  <i className="fa fa-bell"></i>
                  <span className="section-body-title">
                    Text Notification Email
                  </span>
                </div>
                <span className="section-body-title">
                  {notificationEmail === "" ? "-" : notificationEmail}
                </span>
              </div>

              {/* Reply to Email */}

              {((profileUserId && person?.userTypeId === UserRole.Admin) ||
                (!profileUserId && loginUser?.userType === UserRole.Admin)) && (
                <div className="section-body">
                  <div>
                    <i className="fa fa-bell"></i>
                    <span className="section-body-title">Reply to Email</span>
                  </div>
                  <span className="section-body-title">
                    {replyToEmail === "" ? "-" : replyToEmail}
                  </span>
                </div>
              )}

              {showPushNotifications && (
                <div className="section-body">
                  <div>
                    <i className="fa fa-bell"></i>
                    <span className="section-body-title">
                      Push Notifications
                    </span>
                  </div>
                  <button
                    className={`preferences__toggle${pushEnabled ? " preferences__toggle--on" : ""}`}
                    onClick={handlePushToggle}
                    aria-pressed={pushEnabled}
                    aria-label="Push Notifications"
                  />
                </div>
              )}

              <div className="section-body">
                <div>
                  <i className="fa fa-volume-up"></i>
                  <span className="section-body-title">Voice</span>
                </div>
                <span className="section-body-title">{voice}</span>
              </div>

              <div className="section-body">
                <div>
                  <i className="fa fa-phone"></i>
                  <span className="section-body-title">Phone Code</span>
                </div>
                <span className="section-body-title">
                  {phoneCode === "" ? "-" : phoneCode}
                </span>
              </div>
              <div className="section-body">
                <div>
                  <i className="fa fa-phone"></i>
                  <span className="section-body-title">Phone Pin</span>
                </div>
                <span className="section-body-title">
                  {phonePin === "" ? "-" : phonePin}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
